import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    AppBar,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    ClickAwayListener,
    Drawer,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    List,
    ListItem,
    ListItemButton,
    Paper,
    Stack,
    TextField,
    Toolbar,
    Typography
} from '@mui/material'
import Help from '../../assets/icons/Help'
import SearchIcon from '../../assets/icons/SearchIcon'
import { getCategories, getContent, searchAllArticles, searchArticles } from '../../services/apis/help'
import { ArrowForwardIosOutlined } from '@mui/icons-material'
import { primary } from '../../assets/colors'
import { debounce } from 'throttle-debounce'
import { ExtraSmallButton, NonInteractiveTooltip } from '../custom'
import { mergeSideBarBottomButtonSx } from '../sideBarRight/sideBarBottomIconButtonSx'
import {
    createOverlayDrawerOnClose,
    sideBarOverlayDrawerPaperProps,
    sideBarOverlayDrawerProps,
    useSideBarOverlayDrawerEscape
} from '../sideBarRight/sideBarOverlayDrawerProps'
import { useSelector } from 'react-redux'
import { selectCurrentTheme } from '../../selectors/common'
import InfiniteScroll from 'react-infinite-scroll-component'
import { KeyboardShortcutsCategoryView } from './KeyboardShortcutsCategoryView'
import KeyboardShortcutIcon from '../../assets/icons/KeyboardIcon'
import ArrowBackIcon from '../../assets/icons/ArrowBackIcon'
import ClearIcon from '../../assets/icons/ClearIcon'
import AnalyticsManager from '../../analytics/AnalyticsManager'

export function HelpCenter() {
    const [open, setOpen] = useState(false)
    const [categories, setCategories] = useState([])
    const [content, setContent] = useState<any>()
    const [selectedCategory, setSelectedCategory] = useState<any>(null)
    const [selectedFolder, setSelectedFolder] = useState<any>(null)
    const [selectedArticle, setSelectedArticle] = useState<any>(null)
    const [searchText, setSearchText] = useState('')
    const [searchResults, setSearchResults] = useState<any[]>([])
    const [paginatedSearchResults, setPaginatedSearchResults] = useState<any[]>([])
    const [dropdownVisible, setDropdownVisible] = useState(false)
    const [showSearchResults, setShowSearchResults] = useState(false)
    const [pageTitle, setPageTitle] = useState()
    const darkTheme = useSelector(selectCurrentTheme)
    const [totalCount, setTotalCount] = useState<number>(0)
    const pageNo = useRef<number>(1)
    const pageSize = 20
    const [showKeyboardShortcutsView, setShowKeyboardShortcutsView] = useState(false)
    const [showAppBar, setShowAppBar] = useState(false)
    const drawerContentRef = useRef<HTMLDivElement>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            if (drawerContentRef.current) {
                const scrollTop = drawerContentRef.current.scrollTop
                setShowAppBar(scrollTop > 100)
            }
        }

        if (drawerContentRef.current) {
            drawerContentRef.current.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (drawerContentRef.current) {
                drawerContentRef.current.removeEventListener('scroll', handleScroll)
            }
        }
    }, [showKeyboardShortcutsView, selectedCategory, selectedFolder, selectedArticle, showSearchResults])

    useEffect(() => {
        if (selectedArticle && selectedCategory && selectedFolder) {
            setPageTitle(selectedArticle.title)
        } else if (selectedFolder && selectedCategory) {
            setPageTitle(selectedFolder.displayName)
        } else if (selectedCategory) {
            setPageTitle(selectedCategory.displayName)
        }
    }, [selectedCategory, selectedFolder, selectedArticle])

    const handleCategoryClick = (category: any) => {
        setSelectedCategory(category)
    }

    const handleSearch = async () => {
        try {
            const results = await searchArticles({ searchText: searchText, pageNo: 1, pageSize: 5 })
            setSearchResults(results.data)
            pageNo.current = 1
            setTotalCount(results.meta.pagination.total)
            setDropdownVisible(true)
        } catch (error) {
            console.error('Error searching articles:', error)
        }
    }

    const fetchAllArticles = async () => {
        try {
            const results = await searchAllArticles({ searchText: searchText, page: 1, pageSize: pageSize })
            setPaginatedSearchResults(results.data)
            setTotalCount(results.meta.pagination.total)
        } catch (error) {
            console.error('Error searching articles:', error)
        }
    }

    const fetchData = async () => {
        try {
            const results = await searchAllArticles({
                searchText: searchText,
                page: pageNo.current + 1,
                pageSize: pageSize
            })
            setPaginatedSearchResults((prevResults) => [...prevResults, ...results.data])
            setTotalCount(results.meta.pagination.total)
            pageNo.current = pageNo.current + 1
        } catch (error) {
            console.error('Error searching articles:', error)
        }
    }

    const debouncedHandleSearch = useCallback(debounce(300, handleSearch), [searchText])

    useEffect(() => {
        if (searchText === '') {
            setDropdownVisible(false)
        } else {
            debouncedHandleSearch()
        }
        return () => {
            debouncedHandleSearch.cancel()
        }
    }, [searchText, debouncedHandleSearch])

    useEffect(() => {
        const fetchArticleContent = async () => {
            try {
                const content = await getContent({ id: selectedArticle.id })
                setContent(content.content)
            } catch (error) {
                console.error('Error fetching article content:', error)
            } finally {
                setLoading(false)
            }
        }
        if (selectedArticle) {
            fetchArticleContent()
        }
    }, [selectedArticle])

    const handleFolderClick = (folder: any) => {
        setSelectedFolder(folder)
    }

    const handleArticleClick = (article: any) => {
        setLoading(true)
        setSelectedArticle(article)
    }

    const handleBackClick = () => {
        if (selectedArticle) {
            setSelectedArticle(null)
        } else if (selectedFolder) {
            setSelectedFolder(null)
        } else {
            setSelectedCategory(null)
        }
    }

    useEffect(() => {
        if (open) {
            const fetchCategories = async () => {
                try {
                    const categories = await getCategories()
                    setCategories(categories)
                } catch (error) {
                    console.error('Error fetching categories:', error)
                }
            }
            fetchCategories()
        }
    }, [open])

    const toggleDrawer = (open: boolean) => (event: any) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return
        }
        setOpen(open)
        setSelectedCategory(null)
        setSelectedFolder(null)
        setSelectedArticle(null)
        setSearchText('')
        setSearchResults([])
        setPaginatedSearchResults([])
        setShowSearchResults(false)
        setShowKeyboardShortcutsView(false)
    }

    const closeDrawer = useCallback(() => {
        setOpen(false)
        setSelectedCategory(null)
        setSelectedFolder(null)
        setSelectedArticle(null)
        setSearchText('')
        setSearchResults([])
        setPaginatedSearchResults([])
        setShowSearchResults(false)
        setShowKeyboardShortcutsView(false)
    }, [])

    useSideBarOverlayDrawerEscape(open, closeDrawer)

    const handleHelpButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (open) {
            closeDrawer()
        } else {
            toggleDrawer(true)(event)
        }
    }, [open, closeDrawer])

    const handleSearchItemClick = (result: any) => {
        const clickedCategory: any = Object.values(categories).find((category: any) => category.id === result.category)
        if (clickedCategory) {
            const clickedFolder = clickedCategory.folders.find((folder: any) => folder.id === result.folder)
            if (clickedFolder) {
                const clickedArticle = clickedFolder.articles.find((article: any) => article.id === result.id)
                setSelectedCategory(clickedCategory)
                setSelectedFolder(clickedFolder)
                setLoading(true)
                setSelectedArticle(clickedArticle)
                setDropdownVisible(false)
                setSearchText('')
                setShowSearchResults(false)
            }
        }
    }

    const SearchResult = () => {
        return (
            <Stack sx={{ flex: 1, display: 'flex', gap: 2 }}>
                <Stack gap={2} alignItems={'center'} direction={'row'}>
                    <IconButton
                        size="small"
                        sx={{ marginLeft: '-44px' }}
                        data-event={'Back Clicked'}
                        data-event-source={'Help Center'}
                        onClick={() => {
                            setShowSearchResults(false)
                            setSearchText('')
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Stack paddingX={'8px'} gap={2}>
                        <Breadcrumbs aria-label="breadcrumb">
                            <Link
                                color="inherit"
                                href="#"
                                underline="none"
                                fontSize={14}
                                onClick={() => {
                                    setShowSearchResults(false)
                                    setSearchText('')
                                }}
                            >
                                Help Center
                            </Link>
                            <Typography fontSize={14} color="textPrimary">
                                Search results
                            </Typography>
                        </Breadcrumbs>
                        <Typography variant="h5" style={{ fontWeight: '500' }}>
                            Search results
                        </Typography>
                    </Stack>
                </Stack>
                <InfiniteScroll scrollableTarget="searchResultScroll" dataLength={paginatedSearchResults.length} next={fetchData} hasMore={paginatedSearchResults.length < totalCount} loader={<></>}>
                    <List id="searchResultScroll" sx={{ overflowY: 'auto', height: '70vh' }}>
                        {paginatedSearchResults.map((result: any, index: number) => (
                            <ListItem
                                disableGutters={true}
                                disablePadding
                                key={index}
                                sx={{ borderBottom: 0.5, borderColor: 'divider' }}
                                onClick={() => {
                                    setShowSearchResults(false)
                                    handleSearchItemClick(result)
                                }}
                            >
                                <ListItemButton>
                                    <Grid container alignItems={'center'} justifyContent={'space-between'} spacing={1}>
                                        <Grid item xs={10}>
                                            <Typography variant={'body2'}>{result.title}</Typography>
                                        </Grid>

                                        <Grid item xs={2} sx={{ textAlign: 'end' }}>
                                            <ArrowForwardIosOutlined sx={{ fontSize: 12 }} />
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </InfiniteScroll>
            </Stack>
        )
    }

    return (
        <div>
            <NonInteractiveTooltip arrow placement="left" title="Help Center">
                <span>
                    <ExtraSmallButton
                        id="help-section-button"
                        size="small"
                        color="inherit"
                        onClick={handleHelpButtonClick}
                        data-event="Help Center Clicked"
                        sx={mergeSideBarBottomButtonSx(theme => ({
                            backgroundColor: open ? theme.palette.action.selected : 'transparent'
                        }))}
                    >
                        <Help />
                    </ExtraSmallButton>
                </span>
            </NonInteractiveTooltip>
            <Drawer
                anchor="right"
                open={open}
                onClose={createOverlayDrawerOnClose(closeDrawer)}
                {...sideBarOverlayDrawerProps}
                PaperProps={sideBarOverlayDrawerPaperProps}
            >
                {showAppBar && (
                    <AppBar
                        sx={{
                            backgroundColor: (theme) => theme.palette.background.paper,
                            color: 'inherit',
                            borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                        }}
                        position="absolute"
                        elevation={1}
                    >
                        <Toolbar>
                            <Stack paddingLeft={'32px'} width={'100%'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                <IconButton
                                    color="inherit"
                                    data-event={'Back Clicked'}
                                    data-event-source={'Help Center'}
                                    onClick={() => {
                                        setShowAppBar(false)
                                        if (showKeyboardShortcutsView) {
                                            setShowKeyboardShortcutsView(false)
                                        } else {
                                            handleBackClick()
                                        }
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <IconButton color="inherit" onClick={toggleDrawer(false)}>
                                    <ClearIcon />
                                </IconButton>
                            </Stack>
                        </Toolbar>
                    </AppBar>
                )}
                <div
                    ref={drawerContentRef}
                    style={{
                        height: '100%',
                        overflowY: 'auto',
                        padding: '32px',
                        position: 'relative'
                    }}
                >
                    <ExtraSmallButton
                        sx={{
                            position: 'absolute', // Change to 'relative' to make it scroll with content
                            top: '32px',
                            right: '32px',
                            alignSelf: 'flex-end'
                        }}
                        color="inherit"
                        onClick={toggleDrawer(false)}
                    >
                        <ClearIcon sx={{ fontSize: '24px' }} />
                    </ExtraSmallButton>
                    {showKeyboardShortcutsView ? (
                        <KeyboardShortcutsCategoryView
                            onBackClick={() => {
                                setShowKeyboardShortcutsView(false)
                            }}
                        />
                    ) : (
                        <>
                            <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {!selectedCategory && !showSearchResults && <Typography variant="h5">Help Center</Typography>}
                                <Stack>
                                    <div style={{ position: 'relative' }}>
                                        <TextField
                                            variant="standard"
                                            fullWidth
                                            autoComplete="undefined"
                                            sx={(theme) => ({
                                                padding: '8px',
                                                borderRadius: '4px',
                                                border: `1px solid ${theme.palette.divider}`,
                                                fontSize: '2.0rem'
                                            })}
                                            placeholder="Find an answer"
                                            value={searchText}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                                disableUnderline: true
                                            }}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                AnalyticsManager.logEvent('Search Input', { input: event.target.value, source: 'Help Center' })
                                                setSearchText(event.target.value)
                                            }}
                                        />
                                        {dropdownVisible && searchResults.length > 0 && (
                                            <ClickAwayListener
                                                onClickAway={() => {
                                                    setDropdownVisible(false)
                                                    setSearchText('')
                                                }}
                                            >
                                                <Paper
                                                    sx={{
                                                        boxShadow: 3,
                                                        position: 'absolute',
                                                        top: 50,
                                                        width: '100%',
                                                        zIndex: 999
                                                    }}
                                                >
                                                    <List>
                                                        {searchResults.slice(0, 5).map((result, index) => (
                                                            <ListItem key={index} disableGutters={true} disablePadding>
                                                                <ListItemButton
                                                                    onClick={() => {
                                                                        handleSearchItemClick(result)
                                                                    }}
                                                                >
                                                                    <Typography variant="body1" sx={{ flex: 1 }}>
                                                                        {result.title}
                                                                    </Typography>
                                                                    <ArrowForwardIosOutlined sx={{ fontSize: 12 }} />
                                                                </ListItemButton>
                                                            </ListItem>
                                                        ))}
                                                        {totalCount > searchResults.length && (
                                                            <>
                                                                <ListItem disablePadding>
                                                                    <ListItemButton
                                                                        onClick={() => {
                                                                            fetchAllArticles()
                                                                            setShowSearchResults(true)
                                                                            setSelectedCategory(null)
                                                                            setSelectedFolder(null)
                                                                            setSelectedArticle(null)
                                                                            setDropdownVisible(false)
                                                                        }}
                                                                    >
                                                                        <Typography color={primary} variant="body1">
                                                                            See all results
                                                                        </Typography>
                                                                    </ListItemButton>
                                                                </ListItem>
                                                            </>
                                                        )}
                                                    </List>
                                                </Paper>
                                            </ClickAwayListener>
                                        )}
                                    </div>
                                </Stack>
                                {showSearchResults && <SearchResult />}
                                {selectedCategory && (
                                    <Stack gap={2} alignItems={'center'} direction={'row'}>
                                        <IconButton size="small" sx={{ marginLeft: '-44px' }} onClick={handleBackClick} data-event={'Back Clicked'} data-event-source={'Help Center'}>
                                            <ArrowBackIcon />
                                        </IconButton>
                                        <Stack paddingX={'8px'} gap={2}>
                                            <Breadcrumbs itemsBeforeCollapse={1} maxItems={3} aria-label="breadcrumb">
                                                <Link
                                                    color="inherit"
                                                    href="#"
                                                    fontSize={14}
                                                    underline="none"
                                                    onClick={() => {
                                                        setSelectedCategory(null)
                                                        setSelectedFolder(null)
                                                        setSelectedArticle(null)
                                                    }}
                                                >
                                                    Help Center
                                                </Link>
                                                <Link
                                                    color="inherit"
                                                    href="#"
                                                    fontSize={14}
                                                    underline="none"
                                                    onClick={() => {
                                                        setSelectedFolder(null)
                                                        setSelectedArticle(null)
                                                    }}
                                                >
                                                    {selectedCategory.displayName}
                                                </Link>
                                                {selectedFolder && (
                                                    <Link color="inherit" href="#" fontSize={14} underline="none" onClick={() => setSelectedArticle(null)}>
                                                        {selectedFolder.displayName}
                                                    </Link>
                                                )}
                                                {selectedArticle && (
                                                    <Typography fontSize={14} color="textPrimary">
                                                        {selectedArticle.title}
                                                    </Typography>
                                                )}
                                            </Breadcrumbs>
                                            <Typography variant="h5" style={{ fontWeight: '500' }}>
                                                {pageTitle}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                )}
                                <>
                                    {selectedCategory && selectedFolder && selectedArticle ? (
                                        loading ? (
                                            <div>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gridArea: 'content',
                                                        height: '50vh',
                                                        width: '100%'
                                                    }}
                                                >
                                                    <CircularProgress size={50} />
                                                </Box>
                                            </div>
                                        ) : (
                                            <div style={{ paddingLeft: '8px', paddingRight: '8px' }} dangerouslySetInnerHTML={{ __html: content }} />
                                        )
                                    ) : selectedCategory && selectedFolder ? (
                                        <List>
                                            {selectedFolder.articles.map((article: any, index: number) => (
                                                <ListItem
                                                    disableGutters={true}
                                                    disablePadding
                                                    key={index}
                                                    sx={{ borderBottom: 0.5, borderColor: 'divider' }}
                                                    onClick={() => {
                                                        AnalyticsManager.logEvent('Article Clicked', { source: 'Help Center', article_title: article.title })
                                                        handleArticleClick(article)
                                                    }}
                                                >
                                                    <ListItemButton>
                                                        <Grid container alignItems={'center'} justifyContent={'space-between'} spacing={1}>
                                                            <Grid item xs={10}>
                                                                <Stack>
                                                                    <Typography variant={'body2'}>{article.title}</Typography>
                                                                </Stack>
                                                            </Grid>

                                                            <Grid item xs={2} sx={{ textAlign: 'end' }}>
                                                                <ArrowForwardIosOutlined sx={{ fontSize: 12 }} />
                                                            </Grid>
                                                        </Grid>
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : selectedCategory ? (
                                        <List>
                                            {selectedCategory.folders.map((folder: any, index: number) => (
                                                <ListItem
                                                    disableGutters={true}
                                                    disablePadding
                                                    key={index}
                                                    sx={{ borderBottom: 0.5, borderColor: 'divider' }}
                                                    onClick={() => {
                                                        AnalyticsManager.logEvent('Folder Clicked', { source: 'Help Center', category: folder.displayName })
                                                        handleFolderClick(folder)
                                                    }}
                                                >
                                                    <ListItemButton>
                                                        <Grid container alignItems={'center'} justifyContent={'space-between'} spacing={1}>
                                                            <Grid item xs={10}>
                                                                <Stack>
                                                                    <Typography variant={'body2'}>{folder.displayName}</Typography>
                                                                    <Typography noWrap variant={'body2'}>
                                                                        {`${folder.count} articles`}
                                                                    </Typography>
                                                                </Stack>
                                                            </Grid>

                                                            <Grid item xs={2} sx={{ textAlign: 'end' }}>
                                                                <ArrowForwardIosOutlined sx={{ fontSize: 12 }} />
                                                            </Grid>
                                                        </Grid>
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : showSearchResults ? (
                                        <></>
                                    ) : (
                                        <>
                                            <Typography variant="h6">Select your issue category</Typography>
                                            <Grid container spacing={2}>
                                                {Object.values(categories)
                                                    .sort((a: any, b: any) => a.order - b.order)
                                                    .map((category: any, index) => (
                                                        <Grid item xs={12} sm={6} md={6} key={index}>
                                                            <Card
                                                                sx={{ backgroundColor: (theme) => theme.palette.background.default }}
                                                                onClick={() => {
                                                                    AnalyticsManager.logEvent('Category Clicked', { source: 'Help Center', category: category.displayName })
                                                                    handleCategoryClick(category)
                                                                }}
                                                            >
                                                                <CardActionArea>
                                                                    <CardContent sx={{ width: '100%' }}>
                                                                        <Stack direction={'row'} gap={1} justifyContent={'space-between'} alignItems={'center'}>
                                                                            <Box>
                                                                                <Typography variant="h6">{category.displayName}</Typography>
                                                                                <Typography variant="body2">{`${category.count} articles`}</Typography>
                                                                            </Box>
                                                                            <Box
                                                                                component="img"
                                                                                alt={category.displayName}
                                                                                src={category.icon}
                                                                                sx={{
                                                                                    height: '1.5rem',
                                                                                    width: '1.5rem',
                                                                                    filter: darkTheme ? 'invert(1)' : 'none'
                                                                                }}
                                                                            />
                                                                        </Stack>
                                                                    </CardContent>
                                                                </CardActionArea>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                            </Grid>
                                            <Typography variant="h6">Looking for something else?</Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Button
                                                        color={'inherit'}
                                                        sx={{ padding: '24px' }}
                                                        variant={'outlined'}
                                                        onClick={() => {
                                                            setShowKeyboardShortcutsView(true)
                                                        }}
                                                    >
                                                        <Stack justifyContent={'center'} alignItems={'center'} gap={2}>
                                                            <KeyboardShortcutIcon sx={{ fontSize: '120px' }} />
                                                            <Typography align={'center'} fontSize={'18px'}>
                                                                Keyboard Shortcuts
                                                            </Typography>
                                                        </Stack>
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </>
                            </Grid>
                        </>
                    )}
                </div>
            </Drawer>
        </div>
    )
}
